/**
 * Markets board model (docs/markets-ux.md) — pure, side-effect-free, unit-tested in
 * marketsBoard.test.ts. Turns the contest list + each card's liquidity into what a game card
 * shows: the moneyline (one best price per side) for head-to-head games, the three shortest
 * prices for futures, and which section a contest lands in.
 *
 * Built on the same structural shapes as lib/books.ts (PositionLike / PartnerLike) so real
 * `LiquidityPosition`s drop straight in.
 */
import type { PartnerLike, PositionLike } from "./books.js";
import { propSides } from "./books.js";

/* ----------------------------------------------------------------- contests */

/** The subset of a `Contest` the board reads. */
export interface ContestLike {
  id: string;
  type?: string | null;
  title?: string | null;
  starts_at?: string | null;
  league_id?: string | null;
  has_markets?: boolean | null;
  tags?: unknown;
}

export interface Team {
  abbr?: string;
  name?: string;
}

/** For head-to-head contests the two teams are inline on `tags`; no participants join needed. */
export function teamsOf(c: ContestLike): { home: Team; away: Team } | null {
  const tags = (c.tags ?? {}) as Record<string, unknown>;
  const home = tags.home_team as Team | undefined;
  const away = tags.away_team as Team | undefined;
  if (home && away && (home.name || home.abbr) && (away.name || away.abbr)) return { home, away };
  return null;
}

// contest.type drives categorization:
//   team       — team-based event (MLB/NBA/NFL game) → a GAME
//   match      — athlete vs athlete (tennis, UFC) → a GAME
//   tournament — futures / outright (season winner) → not a game
export const isGame = (c: ContestLike): boolean => c.type === "team" || c.type === "match";
/** "@" reads right for team sports (away @ home); "vs" for athlete-vs-athlete. */
export const matchupSep = (c: ContestLike): string => (c.type === "match" ? "vs" : "@");
export const teamLabel = (t: Team): string => t.name ?? t.abbr ?? "—";

/* --------------------------------------------------------------- actionable */

/**
 * A venue quote you can actually buy right now: the (undocumented, observed-live)
 * `actionable` flag isn't false, and it quotes a real price inside (0, 1) with size behind it.
 * Live payloads carry `actionable:false` rows with price 0 / available 0 (a venue listing the
 * outcome with nothing on offer) — those never count.
 */
export function isActionable(pl: PartnerLike | null | undefined): boolean {
  if (!pl) return false;
  if ((pl as { actionable?: unknown }).actionable === false) return false;
  return (
    typeof pl.price === "number" &&
    pl.price > 0 &&
    pl.price < 1 &&
    typeof pl.available === "number" &&
    pl.available > 0
  );
}

/**
 * The best venue to BUY a position at: the lowest actionable price (ties → the deepest book),
 * optionally limited to a set of partner_ids (Real mode: the user's connected venues). Same
 * rule as the game page's best-price book.
 */
export function bestQuote<P extends PartnerLike>(
  pos: { partner_liquidities?: P[] },
  allowed: ReadonlySet<string> | null = null,
): P | null {
  let best: P | null = null;
  for (const pl of pos.partner_liquidities ?? []) {
    if (!isActionable(pl)) continue;
    if (allowed && !allowed.has(pl.partner_id)) continue;
    if (
      !best ||
      (pl.price as number) < (best.price as number) ||
      (pl.price === best.price && (pl.available ?? 0) > (best.available ?? 0))
    ) {
      best = pl;
    }
  }
  return best;
}

const hasActionable = (p: PositionLike): boolean => (p.partner_liquidities ?? []).some(isActionable);

/* ----------------------------------------------------- picking the card market */

/**
 * Head-to-head "who wins" market keys, in priority order. Observed live (2026-10-28):
 * `moneyline` (NBA/NFL/NHL/MLB/NCAAF/WNBA), `athlete_match_winner` (ATP, UFC) and
 * `game_result_3way` (soccer: home / draw / away as yes/no per participant). The docs only
 * show `moneyline` — see FRICTION.md.
 */
export const MONEYLINE_KEYS = ["moneyline", "athlete_match_winner", "team_match_winner", "match_winner", "game_result_3way"];
/** Fallback for an unlisted winner market: `*_match_winner`, `*_moneyline`, `*_game_winner`. */
const MONEYLINE_RE = /(^|_)(moneyline|match_winner|game_winner)$/;
/** Period/half markets also end in these words — never take them as the game's moneyline. */
export const PERIOD_RE = /^(q\d|p\d|first_|second_|1st_|2nd_|half_)/;

function groupByMarket(positions: PositionLike[]): Map<string, PositionLike[]> {
  const m = new Map<string, PositionLike[]>();
  for (const p of positions) {
    const k = p.market_key ?? "(unknown)";
    let g = m.get(k);
    if (!g) m.set(k, (g = []));
    g.push(p);
  }
  return m;
}

/** The moneyline market_key of a game's liquidity (with some actionable price), or null. */
export function moneylineKey(positions: PositionLike[]): string | null {
  const groups = groupByMarket(positions);
  const live = (k: string) => (groups.get(k) ?? []).some(hasActionable);
  for (const k of MONEYLINE_KEYS) if (live(k)) return k;
  for (const k of groups.keys()) if (MONEYLINE_RE.test(k) && !PERIOD_RE.test(k) && live(k)) return k;
  return null;
}

/** A futures contest's main market: the market_key with the most actionable participants. */
export function futuresKey(positions: PositionLike[]): string | null {
  let best: string | null = null;
  let bestN = 0;
  for (const [k, group] of groupByMarket(positions)) {
    const n = new Set(group.filter(hasActionable).map((p) => p.participant_id ?? p.position_hash)).size;
    if (n > bestN) {
      best = k;
      bestN = n;
    }
  }
  return best;
}

/**
 * What one card keeps from a contest's liquidity. A busy NBA game returns ~2,000 positions
 * (~3.4 MB) and the card needs two of them, so the card's cache holds only its market plus a
 * count of the other tradeable markets. `positions` are untouched originals, so they go
 * straight into TradeTicket and the stream bridge can patch them in place.
 */
export interface CardLiquidity {
  positions: PositionLike[];
  /** Other markets on the contest with an actionable price (spreads, totals, props…). */
  otherMarkets: number;
  /** Venues with an actionable price anywhere on the contest. */
  venues: number;
}

export function trimForCard(positions: PositionLike[], game: boolean): CardLiquidity {
  const key = game ? moneylineKey(positions) : futuresKey(positions);
  const kept: PositionLike[] = [];
  const otherKeys = new Set<string>();
  const venues = new Set<string>();
  for (const p of positions) {
    const live = (p.partner_liquidities ?? []).filter(isActionable);
    for (const pl of live) venues.add(pl.partner_id);
    if (key != null && p.market_key === key) kept.push(p);
    else if (live.length > 0) otherKeys.add(p.market_key ?? "(unknown)");
  }
  return { positions: kept, otherMarkets: otherKeys.size, venues: venues.size };
}

/* ----------------------------------------------------------------- card model */

export interface CardQuote {
  /** Stable key for the button (position_hash). */
  key: string;
  label: string;
  position: PositionLike;
  /** Best venue in scope, or null when nothing in scope quotes this outcome. */
  best: PartnerLike | null;
}

export type CardModel =
  | { kind: "moneyline"; quotes: CardQuote[]; venues: number; tradeable: true }
  | { kind: "futures"; quotes: CardQuote[]; more: number; venues: number; tradeable: true }
  /** Tradeable, but not on the card's market (e.g. a game priced only on spreads). */
  | { kind: "elsewhere"; otherMarkets: number; venues: number; tradeable: true }
  | { kind: "none"; tradeable: false };

const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();

/** Pick one position per outcome: the one with the cheapest best quote (dup market_side_ids). */
function bestPerOutcome(
  positions: PositionLike[],
  outcomeOf: (p: PositionLike) => string | null,
  allowed: ReadonlySet<string> | null,
): Map<string, { pos: PositionLike; best: PartnerLike | null }> {
  const out = new Map<string, { pos: PositionLike; best: PartnerLike | null }>();
  for (const p of positions) {
    if (!hasActionable(p)) continue;
    const k = outcomeOf(p);
    if (k == null) continue;
    const best = bestQuote(p, allowed);
    const prev = out.get(k);
    const better =
      !prev ||
      (best && !prev.best) ||
      (best && prev.best && (best.price as number) < (prev.best.price as number));
    if (better) out.set(k, { pos: p, best });
  }
  return out;
}

/**
 * The card for a contest. `allowed` limits best prices to those venues (Real mode); whether the
 * card is tradeable at all is decided across every venue, so the board doesn't hide a game just
 * because none of your own venues list it.
 */
export function buildCardModel(
  contest: ContestLike,
  data: CardLiquidity,
  allowed: ReadonlySet<string> | null = null,
): CardModel {
  const positions = data.positions.filter(hasActionable);
  if (positions.length === 0) {
    return data.otherMarkets > 0
      ? { kind: "elsewhere", otherMarkets: data.otherMarkets, venues: data.venues, tradeable: true }
      : { kind: "none", tradeable: false };
  }
  return isGame(contest)
    ? moneylineModel(contest, positions, data.venues, allowed)
    : futuresModel(positions, data.venues, allowed);
}

function moneylineModel(
  contest: ContestLike,
  positions: PositionLike[],
  venues: number,
  allowed: ReadonlySet<string> | null,
): CardModel {
  const sides = new Set(positions.map((p) => norm(p.side_key)));
  const teams = teamsOf(contest);

  // 3-way soccer result: yes/no per participant (home, Draw, away). The card offers the three
  // "yes" outcomes, ordered like the card's team lines: away, draw, home.
  if (sides.has("yes")) {
    const yes = bestPerOutcome(
      positions.filter((p) => norm(p.side_key) === "yes"),
      (p) => p.participant_id ?? p.position_hash,
      allowed,
    );
    const rank = (p: PositionLike): number => {
      const n = norm(p.participant_name);
      if (teams && n && (n === norm(teams.away.name) || n === norm(teams.away.abbr))) return 0;
      if (n === "draw" || n === "tie") return 1;
      if (teams && n && (n === norm(teams.home.name) || n === norm(teams.home.abbr))) return 2;
      return 3;
    };
    const quotes = [...yes.values()]
      .sort((a, b) => rank(a.pos) - rank(b.pos))
      .map(({ pos, best }) => ({ key: pos.position_hash, label: pos.participant_name ?? pos.title ?? "—", position: pos, best }));
    return { kind: "moneyline", quotes, venues, tradeable: true };
  }

  // Two-way: one best price per side, away first (the card reads "away @ home").
  const bySide = bestPerOutcome(positions, (p) => norm(p.side_key) || null, allowed);
  const order = (s: string) => (s === "away" ? 0 : s === "home" ? 2 : 1);
  const quotes = [...bySide.entries()]
    .sort(([a], [b]) => order(a) - order(b))
    .map(([side, { pos, best }]) => {
      const team = side === "home" ? teams?.home : side === "away" ? teams?.away : undefined;
      return {
        key: pos.position_hash,
        label: pos.participant_name ?? (team ? teamLabel(team) : (pos.title ?? side)),
        position: pos,
        best,
      };
    });
  return { kind: "moneyline", quotes, venues, tradeable: true };
}

/** How many futures rows a card shows. */
export const FUTURES_TOP = 3;

/**
 * Futures / multi-outcome: one row per participant on the affirmative side (yes / over / home),
 * the three SHORTEST prices first — the favourites, i.e. the highest buy price — then "+N more".
 */
export function futuresModel(
  positions: PositionLike[],
  venues: number,
  allowed: ReadonlySet<string> | null = null,
  top = FUTURES_TOP,
): CardModel {
  const side = propSides(positions)[0];
  const rows = bestPerOutcome(
    positions.filter((p) => side == null || norm(p.side_key) === side),
    (p) => p.participant_id ?? p.position_hash,
    allowed,
  );
  // Rank by the all-venue best so Real-mode scoping never reshuffles who the favourites are.
  const sortPrice = (p: PositionLike) => (bestQuote(p)?.price as number | undefined) ?? 0;
  const all = [...rows.values()].sort(
    (a, b) =>
      sortPrice(b.pos) - sortPrice(a.pos) ||
      (a.pos.participant_name ?? "").localeCompare(b.pos.participant_name ?? ""),
  );
  const affirmative = side == null || side === "yes" || side === "home";
  const quotes = all.slice(0, top).map(({ pos, best }) => ({
    key: pos.position_hash,
    label: affirmative ? (pos.participant_name ?? pos.title ?? "—") : (pos.title ?? pos.participant_name ?? "—"),
    position: pos,
    best,
  }));
  return { kind: "futures", quotes, more: Math.max(0, all.length - top), venues, tradeable: true };
}

/* ------------------------------------------------------------------ sections */

export type Bucket = "live" | "soon" | "today" | "tomorrow" | "later" | "futures" | "earlier";

export const SECTION_ORDER: { key: Bucket; label: string }[] = [
  { key: "live", label: "Live now" },
  { key: "soon", label: "Starting soon" },
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "later", label: "Later" },
  { key: "futures", label: "Futures & outrights" },
  { key: "earlier", label: "Started earlier" },
];

const MIN = 60_000;
const HOUR = 60 * MIN;
/** "Starting soon" = within this long of now. */
export const SOON_MS = 3 * HOUR;
/**
 * With no game state to read (signed out, or no gamestate scope) an open game that started up
 * to this long ago is presumed in progress. Older ones go to "Started earlier".
 */
export const PRESUMED_LIVE_MS = 4 * HOUR;

/** Live-ness from contest state: true / false when known, null when there's no state to read. */
export type LiveSignal = boolean | null;

function startOfDay(t: number, addDays = 0): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + addDays);
  return d.getTime();
}

export function startMs(c: ContestLike): number {
  const t = c.starts_at ? Date.parse(c.starts_at) : NaN;
  return Number.isFinite(t) ? t : NaN;
}

/** Which section a contest lands in. Local-time day boundaries. */
export function bucketOf(c: ContestLike, live: LiveSignal, now: number): Bucket {
  if (!isGame(c)) return "futures";
  if (live === true) return "live";
  const start = startMs(c);
  if (!Number.isFinite(start)) return "later";
  if (start <= now) {
    const ago = now - start;
    if (live === null && ago < PRESUMED_LIVE_MS) return "live"; // presumed in progress
    if (live === false && ago < PRESUMED_LIVE_MS) return "soon"; // state says not started yet
    return "earlier";
  }
  if (start - now <= SOON_MS) return "soon";
  if (start < startOfDay(now, 1)) return "today";
  if (start < startOfDay(now, 2)) return "tomorrow";
  return "later";
}

export interface Section<C> {
  key: Bucket;
  label: string;
  contests: C[];
}

/**
 * Group contests into the board's sections, in display order, dropping empty ones.
 * Within a section: soonest start first. Live now: most recently started first. Futures:
 * alphabetical (their start dates are season openers, not useful to sort on).
 */
export function bucketContests<C extends ContestLike>(
  contests: C[],
  liveOf: (c: C) => LiveSignal,
  now: number,
): Section<C>[] {
  const g = new Map<Bucket, C[]>();
  for (const c of contests) {
    const b = bucketOf(c, liveOf(c), now);
    let arr = g.get(b);
    if (!arr) g.set(b, (arr = []));
    arr.push(c);
  }
  const asc = (a: C, b: C) => (startMs(a) || Infinity) - (startMs(b) || Infinity) || a.id.localeCompare(b.id);
  const sections: Section<C>[] = [];
  for (const { key, label } of SECTION_ORDER) {
    const arr = g.get(key);
    if (!arr?.length) continue;
    if (key === "live") arr.sort((a, b) => -asc(a, b));
    else if (key === "futures") arr.sort((a, b) => (a.title ?? "").localeCompare(b.title ?? "") || a.id.localeCompare(b.id));
    else arr.sort(asc);
    sections.push({ key, label, contests: arr });
  }
  return sections;
}

/* ------------------------------------------------------------- tradeable only */

/** A card's price status: not loaded yet, loaded with something to trade, nothing, or failed. */
export type PriceStatus = "pending" | "tradeable" | "empty" | "error";

/**
 * The cards to render. With "tradeable only" on (the default), drop cards known to have
 * nothing to trade — `has_markets: false` straight from the list, or a loaded liquidity with no
 * actionable price. Pending and errored cards stay (they show skeleton / retry).
 */
export function visibleContests<C extends ContestLike>(
  contests: C[],
  statusOf: (c: C) => PriceStatus,
  tradeableOnly: boolean,
): C[] {
  if (!tradeableOnly) return contests;
  return contests.filter((c) => c.has_markets !== false && statusOf(c) !== "empty");
}

/** Price status for a card from its query state + model. */
export function priceStatus(
  contest: ContestLike,
  q: { data?: CardLiquidity; isError?: boolean } | undefined,
): PriceStatus {
  if (contest.has_markets === false) return "empty";
  if (q?.data) return buildCardModel(contest, q.data).tradeable ? "tradeable" : "empty";
  if (q?.isError) return "error";
  return "pending";
}
